import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Client } from '../types';
import { Building2, Calendar, Plus, ChevronDown, ArrowRight, Download, Upload, Edit, Edit2 } from 'lucide-react';

interface Props {
  clients: Client[];
  activeClient: Client | null;
  activePeriod: string; // Keep as string for backward compat, but treated as 'YYYY-MM' or 'YYYY-MM:YYYY-MM'
  onClientChange: (client: Client) => void;
  onPeriodChange: (period: string) => void;
  onAddClient: (client: Client) => void;
  onUpdateClient: (client: Client) => void;
  onBulkAddClients: (clients: Client[]) => void;
}

const ContextSelector: React.FC<Props> = ({
  clients,
  activeClient,
  activePeriod,
  onClientChange,
  onPeriodChange,
  onAddClient,
  onUpdateClient,
  onBulkAddClients
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [clientFormName, setClientFormName] = useState('');
  const [clientFormGstin, setClientFormGstin] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  // Parse activePeriod to start/end
  const [startPeriod, setStartPeriod] = useState(activePeriod.split(':')[0] || new Date().toISOString().slice(0, 7));
  const [endPeriod, setEndPeriod] = useState(activePeriod.split(':')[1] || activePeriod.split(':')[0] || new Date().toISOString().slice(0, 7));
  const [isRangeMode, setIsRangeMode] = useState(activePeriod.includes(':'));

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update parent when local state changes
  useEffect(() => {
    if (isRangeMode) {
      if (startPeriod > endPeriod) {
        onPeriodChange(`${endPeriod}:${startPeriod}`);
      } else {
        onPeriodChange(`${startPeriod}:${endPeriod}`);
      }
    } else {
      onPeriodChange(startPeriod);
    }
  }, [startPeriod, endPeriod, isRangeMode]);

  const openAddModal = () => {
    setIsAdding(true);
    setIsEditing(false);
    setEditingId(null);
    setClientFormName('');
    setClientFormGstin('');
  };

  const openEditModal = () => {
    if (!activeClient) return;
    setIsAdding(true);
    setIsEditing(true);
    setEditingId(activeClient.id);
    setClientFormName(activeClient.name);
    setClientFormGstin(activeClient.gstin);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (clientFormName && clientFormGstin) {
      if (isEditing && editingId) {
        onUpdateClient({
          id: editingId,
          name: clientFormName,
          gstin: clientFormGstin
        });
      } else {
        onAddClient({
          id: Math.random().toString(36).substr(2, 9),
          name: clientFormName,
          gstin: clientFormGstin
        });
      }
      setIsAdding(false);
      setClientFormName('');
      setClientFormGstin('');
      setIsEditing(false);
      setEditingId(null);
    }
  };

  const handleFYChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const year = parseInt(e.target.value);
    if (!isNaN(year)) {
      setStartPeriod(`${year}-04`);
      setEndPeriod(`${year + 1}-03`);
    }
  };

  const handleTemplateDownload = () => {

    const ws = XLSX.utils.aoa_to_sheet([['Client Name', 'GSTIN'], ['Acme Corp', '27AAAAA0000A1Z5']]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Clients");
    XLSX.writeFile(wb, "Client_Import_Template.xlsx");
  };

  const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target?.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
        // Assume Row 0 is header
        const newClients: Client[] = [];
        for (let i = 1; i < data.length; i++) {
          const row = data[i] as string[];
          if (row[0] && row[1]) {
            newClients.push({
              id: Math.random().toString(36).substr(2, 9),
              name: row[0],
              gstin: row[1]
            });
          }
        }
        if (newClients.length > 0) {
          onBulkAddClients(newClients);
          setIsAdding(false);
          alert(`Imported ${newClients.length} clients successfully.`);
        } else {
          alert("No valid client records found.");
        }
      } catch (err) {
        console.error(err);
        alert("Failed to parse file.");
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = ''; // Reset input
  };

  // FY Options starting from 2017-18 (GST Start)
  const currentYear = new Date().getFullYear();
  const startYear = 2017;
  const fyOptions = Array.from({ length: currentYear - startYear + 2 }, (_, i) => startYear + i);

  return (
    <div className="bg-white border-b border-slate-200 px-4 py-3 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 shadow-sm z-10 w-full">
      <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6 w-full">

        {/* Client Selector */}
        <div className="relative group w-full md:w-auto">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-bold tracking-wider mb-1">
            <Building2 size={12} /> Client
          </div>
          <div className="flex items-center gap-2 w-full">
            <div className="relative w-full md:w-64">
              <select
                id="client-selector"
                name="client-selector"
                className="appearance-none bg-slate-50 border border-slate-300 hover:border-blue-400 text-slate-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 pr-8 font-semibold transition-colors cursor-pointer truncate"
                value={activeClient?.id || ''}
                onChange={(e) => {
                  const c = clients.find(cl => cl.id === e.target.value);
                  if (c) onClientChange(c);
                }}
              >
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-3 text-slate-400 pointer-events-none" />
            </div>
            {activeClient && (
              <button
                onClick={openEditModal}
                className="p-2 text-slate-400 hover:text-blue-600 hover:bg-slate-100 rounded-md transition-colors"
                title="Edit Client Details"
              >
                <Edit2 size={18} />
              </button>
            )}
          </div>
        </div>

        {/* Period Selector */}
        <div className="relative w-full md:w-auto mt-2 md:mt-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-bold tracking-wider">
              <Calendar size={12} /> Tax Period
            </div>
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                id="multi-month-checkbox"
                name="multi-month-checkbox"
                type="checkbox"
                checked={isRangeMode}
                onChange={(e) => setIsRangeMode(e.target.checked)}
                className="w-3 h-3 rounded text-blue-600 focus:ring-0"
              />
              <span className="text-[10px] text-blue-600 font-medium select-none">Multi-Month</span>
            </label>
          </div>

          <div className="flex flex-wrap md:flex-nowrap items-center gap-2 w-full">
            <input
              id="start-period"
              name="start-period"
              type="month"
              value={startPeriod}
              onChange={(e) => setStartPeriod(e.target.value)}
              className="bg-slate-50 border border-slate-300 hover:border-blue-400 text-slate-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full md:w-40 p-2.5 font-semibold transition-colors cursor-pointer"
            />
            {isRangeMode && (
              <>
                <span className="text-slate-400 hidden md:inline"><ArrowRight size={14} /></span>
                <span className="text-slate-400 md:hidden w-full text-center text-xs">to</span>
                <input
                  id="end-period"
                  name="end-period"
                  type="month"
                  value={endPeriod}
                  onChange={(e) => setEndPeriod(e.target.value)}
                  className="bg-slate-50 border border-slate-300 hover:border-blue-400 text-slate-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full md:w-40 p-2.5 font-semibold transition-colors cursor-pointer"
                />
                <select
                  className="bg-white border border-slate-300 text-xs rounded-lg px-2 py-2.5 outline-none focus:border-blue-500 hover:border-blue-400 font-medium text-slate-700 cursor-pointer w-full md:w-auto"
                  onChange={handleFYChange}
                  defaultValue=""
                >
                  <option value="" disabled>Select FY</option>
                  {fyOptions.map(y => (
                    <option key={y} value={y}>FY {y}-{(y + 1).toString().slice(2)}</option>
                  ))}
                </select>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Add Client Button */}
      <div className="flex items-end mt-2 md:mt-0 w-full md:w-auto">
        <button
          onClick={openAddModal}
          className="flex items-center justify-center gap-2 text-sm font-medium text-blue-600 hover:bg-blue-50 px-3 py-2.5 rounded-lg transition-colors border border-blue-200 w-full md:w-auto md:border-transparent"
        >
          <Plus size={16} /> New Client
        </button>
      </div>

      {/* Add/Edit Client Modal/Popover */}
      {isAdding && (
        <div className="absolute top-20 right-6 w-80 bg-white shadow-xl rounded-xl border border-slate-200 p-4 z-50 animate-in fade-in slide-in-from-top-2">
          <h3 className="font-bold text-slate-800 mb-3">{isEditing ? 'Edit Client' : 'Add New Client'}</h3>
          <form onSubmit={handleFormSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Entity Name</label>
              <input
                id="client-name"
                name="client-name"
                autoFocus
                type="text"
                required
                className="w-full border border-slate-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                placeholder="e.g. Acme Corp Ltd"
                value={clientFormName}
                onChange={e => setClientFormName(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">GSTIN</label>
              <input
                id="client-gstin"
                name="client-gstin"
                type="text"
                required
                className="w-full border border-slate-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none uppercase"
                placeholder="27ABCDE1234F1Z5"
                value={clientFormGstin}
                onChange={e => setClientFormGstin(e.target.value)}
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button
                type="button"
                onClick={() => setIsAdding(false)}
                className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
              >
                {isEditing ? 'Update' : 'Create'}
              </button>
            </div>
          </form>

          {!isEditing && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <h4 className="text-xs font-bold text-slate-600 mb-2">Or Import Bulk</h4>
              <div className="flex gap-2">
                <button type="button" onClick={() => fileInputRef.current?.click()} className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded">
                  <Upload size={12} /> Select File
                </button>
                <button type="button" onClick={handleTemplateDownload} className="flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded" title="Download Template">
                  <Download size={12} />
                </button>
                <input id="client-bulk-import" name="client-bulk-import" type="file" ref={fileInputRef} className="hidden" accept=".xlsx,.xls,.csv" onChange={handleFileImport} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContextSelector;